import {
  LabelElement,
  TextElement,
  BarcodeElement,
  QRCodeElement,
  DataMatrixElement,
  LineElement,
  RectangleElement,
  CircleElement,
  LabelConfig,
  Rotation,
  ZebraFont,
} from '@/types'
import { DPI } from '@/lib/utils/units'
import { generateId } from '@/lib/utils/generateId'

interface ParseResult {
  label: Partial<LabelConfig>
  elements: LabelElement[]
  warnings: string[]
}

interface FieldState {
  x: number // in dots
  y: number // in dots
  font: ZebraFont
  fontSize: number
  fontWidth: number
  rotation: Rotation
  // Barcode defaults from ^BY
  barcodeModuleWidth: number
  barcodeWidthRatio: number
  barcodeHeight: number
}

// Convert dots to mm
function dotsToMm(dots: number, dpi: DPI): number {
  return (dots / dpi) * 25.4
}

// Convert ZPL font height (in dots) to a visual font size for the designer
// ZPL bitmap fonts are specified in dots height. To get a visually similar
// size in the designer, we convert dots to mm and then use a scaling factor.
// The designer renders fonts where 1pt ≈ 0.35mm, so we need to find
// a font size that produces similar visual height.
function dotsToFontSize(dots: number, dpi: DPI): number {
  // Convert dots to mm (actual physical size)
  const heightMm = (dots / dpi) * 25.4
  // Convert mm to approximate font points
  // 1 pt = 1/72 inch = 0.3528 mm
  // But fonts have ascenders/descenders, so visible height ≈ 70% of font size
  // We use a factor that produces visually similar results
  const fontSize = heightMm / 0.35 // roughly 1pt = 0.35mm for cap height
  return Math.round(fontSize)
}

// Parse rotation character
function parseRotation(r: string | undefined): Rotation {
  switch (r?.toUpperCase()) {
    case 'R': return 'R'
    case 'I': return 'I'
    case 'B': return 'B'
    default: return 'N'
  }
}

// Parse font letter
function parseFont(f: string): ZebraFont {
  const upper = f?.toUpperCase()
  if (['0', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].includes(upper)) {
    return upper as ZebraFont
  }
  return '0'
}

// Parse comma-separated parameters, handling optional ones
function parseParams(params: string): string[] {
  return params.split(',').map(p => p.trim())
}

// Parse barcode command where rotation may be attached (e.g., ^BCN,50 or ^BC,N,50)
function parseBarcodeCmd(afterCmd: string): { rotation: Rotation; params: string[] } {
  let rotation: Rotation = 'N'
  let paramStr = afterCmd

  // Check if first char is rotation letter (N, R, I, B) followed by comma or end
  if (afterCmd.length > 0 && /^[NRIB]/i.test(afterCmd[0])) {
    // It's a rotation only if followed by comma or nothing
    if (afterCmd.length === 1 || afterCmd[1] === ',') {
      rotation = parseRotation(afterCmd[0])
      paramStr = afterCmd.substring(afterCmd[1] === ',' ? 2 : 1)
    }
  }

  const params = parseParams(paramStr)
  return { rotation, params }
}

export function parseZPL(zplCode: string, dpi: DPI = 203): ParseResult {
  const result: ParseResult = {
    label: { dpi },
    elements: [],
    warnings: [],
  }

  // Current field state
  const state: FieldState = {
    x: 0,
    y: 0,
    font: '0',
    fontSize: 30,
    fontWidth: 0,
    rotation: 'N',
    barcodeModuleWidth: 2,
    barcodeWidthRatio: 3,
    barcodeHeight: 10,
  }

  // Pending field data
  let pendingField: {
    type: string
    params: string[]
    rotation?: Rotation
  } | null = null

  // Track unsupported commands to avoid duplicate warnings
  const unsupportedCommands = new Set<string>()

  // Normalize ZPL - keep structure but remove excessive whitespace
  const normalized = zplCode
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')

  // Split into commands more carefully
  // Commands start with ^ or ~ and continue until the next ^ or ~
  const commands: string[] = []
  let currentCmd = ''
  let inCommand = false

  for (let i = 0; i < normalized.length; i++) {
    const char = normalized[i]

    if (char === '^' || char === '~') {
      if (currentCmd.trim()) {
        commands.push(currentCmd.trim())
      }
      currentCmd = char
      inCommand = true
    } else if (inCommand) {
      currentCmd += char
    }
  }
  if (currentCmd.trim()) {
    commands.push(currentCmd.trim())
  }

  for (const cmd of commands) {
    if (cmd.length < 2) continue

    const cmdCode = cmd.substring(0, 3).toUpperCase().replace(/[^A-Z0-9]/g, '')
    const cmdPrefix = cmd.substring(0, 3)

    try {
      // Skip comments
      if (cmd.toUpperCase().startsWith('^FX')) {
        continue
      }

      // Label width ^PW
      if (cmd.toUpperCase().startsWith('^PW')) {
        const width = parseInt(cmd.substring(3))
        if (!isNaN(width)) {
          result.label.width = dotsToMm(width, dpi)
        }
      }
      // Label length ^LL
      else if (cmd.toUpperCase().startsWith('^LL')) {
        const height = parseInt(cmd.substring(3))
        if (!isNaN(height)) {
          result.label.height = dotsToMm(height, dpi)
        }
      }
      // Field origin ^FO
      else if (cmd.toUpperCase().startsWith('^FO')) {
        const params = parseParams(cmd.substring(3))
        state.x = parseInt(params[0]) || 0
        state.y = parseInt(params[1]) || 0
        if (params[2]) {
          state.rotation = parseRotation(params[2])
        }
      }
      // Field typeset ^FT (similar to ^FO but with different baseline behavior)
      else if (cmd.toUpperCase().startsWith('^FT')) {
        const params = parseParams(cmd.substring(3))
        state.x = parseInt(params[0]) || 0
        state.y = parseInt(params[1]) || 0
        if (params[2]) {
          state.rotation = parseRotation(params[2])
        }
      }
      // Font selection ^CF
      else if (cmd.toUpperCase().startsWith('^CF')) {
        const params = parseParams(cmd.substring(3))
        state.font = parseFont(params[0])
        if (params[1]) {
          state.fontSize = parseInt(params[1]) || 30
        }
        if (params[2]) {
          state.fontWidth = parseInt(params[2]) || 0
        }
      }
      // Scalable/bitmapped font ^A
      else if (cmd.toUpperCase().startsWith('^A') && !cmd.toUpperCase().startsWith('^AF')) {
        // ^Afo,h,w where f=font, o=orientation, h=height, w=width
        const match = cmd.match(/\^A([0-9A-H@])([NRIB])?(?:,(\d+))?(?:,(\d+))?/i)
        if (match) {
          state.font = parseFont(match[1])
          if (match[2]) state.rotation = parseRotation(match[2])
          if (match[3]) state.fontSize = parseInt(match[3])
          if (match[4]) state.fontWidth = parseInt(match[4])
        }
      }
      // Barcode field default ^BY
      else if (cmd.toUpperCase().startsWith('^BY')) {
        const params = parseParams(cmd.substring(3))
        if (params[0]) state.barcodeModuleWidth = parseInt(params[0]) || 2
        if (params[1]) state.barcodeWidthRatio = parseFloat(params[1]) || 3
        if (params[2]) state.barcodeHeight = parseInt(params[2]) || 10 // height in dots
      }
      // Code 128 barcode ^BC
      else if (cmd.toUpperCase().startsWith('^BC')) {
        // ^BCo,h,f,g,e,m where h=height in dots
        const params = parseParams(cmd.substring(3))
        const rotation = parseRotation(params[0])
        // Height from ^BC param, or from ^BY, or default 100 dots
        const parsedHeight = params[1] ? parseInt(params[1]) : NaN
        const height = !isNaN(parsedHeight) ? parsedHeight : (state.barcodeHeight || 100)
        const showText = params[2]?.toUpperCase() !== 'N'

        pendingField = {
          type: 'barcode-code128',
          params: [String(height), showText ? 'Y' : 'N', String(state.barcodeModuleWidth)],
          rotation: rotation,
        }
      }
      // Code 39 barcode ^B3
      else if (cmd.toUpperCase().startsWith('^B3')) {
        const params = parseParams(cmd.substring(3))
        const rotation = parseRotation(params[0])
        const checkDigit = params[1]?.toUpperCase() === 'Y'
        const parsedHeight = params[2] ? parseInt(params[2]) : NaN
        const height = !isNaN(parsedHeight) ? parsedHeight : (state.barcodeHeight || 100)
        const showText = params[3]?.toUpperCase() !== 'N'

        pendingField = {
          type: 'barcode-code39',
          params: [String(height), showText ? 'Y' : 'N', String(state.barcodeModuleWidth)],
          rotation: rotation,
        }
      }
      // EAN-13 barcode ^BE
      else if (cmd.toUpperCase().startsWith('^BE')) {
        const params = parseParams(cmd.substring(3))
        const rotation = parseRotation(params[0])
        const parsedHeight = params[1] ? parseInt(params[1]) : NaN
        const height = !isNaN(parsedHeight) ? parsedHeight : (state.barcodeHeight || 100)
        const showText = params[2]?.toUpperCase() !== 'N'

        pendingField = {
          type: 'barcode-ean13',
          params: [String(height), showText ? 'Y' : 'N', String(state.barcodeModuleWidth)],
          rotation: rotation,
        }
      }
      // QR Code ^BQ
      else if (cmd.toUpperCase().startsWith('^BQ')) {
        const params = parseParams(cmd.substring(3))
        const rotation = parseRotation(params[0])
        const model = params[1] || '2'
        const magnification = parseInt(params[2]) || 4
        const ecLevel = params[3]?.toUpperCase() || 'M'

        pendingField = {
          type: 'qrcode',
          params: [model, String(magnification), ecLevel],
          rotation: rotation,
        }
      }
      // Data Matrix ^BX
      else if (cmd.toUpperCase().startsWith('^BX')) {
        // ^BXo,h,s,c,r,f,g where o=orientation, h=height (module size), s=quality level
        // Example: ^BXN,9,200 means N=normal orientation, 9=module size, 200=quality
        const { rotation, params } = parseBarcodeCmd(cmd.substring(3))
        const size = parseInt(params[0]) || 4

        pendingField = {
          type: 'datamatrix',
          params: [String(size)],
          rotation: rotation,
        }
      }
      // Field data ^FD
      else if (cmd.toUpperCase().startsWith('^FD')) {
        // Extract content - everything after ^FD until end or ^FS
        let content = cmd.substring(3)
        // Remove trailing ^FS if present
        const fsIndex = content.toUpperCase().indexOf('^FS')
        if (fsIndex !== -1) {
          content = content.substring(0, fsIndex)
        }
        content = content.trim()

        if (pendingField) {
          // Create element based on pending field type
          const element = createElementFromField(pendingField, content, state, dpi, result)
          if (element) {
            result.elements.push(element)
          }
          pendingField = null
        } else if (content) {
          // Plain text field
          // Convert font size from dots to visual font size
          const fontSizeInPoints = dotsToFontSize(state.fontSize, dpi)
          const textElement: TextElement = {
            id: generateId(),
            type: 'text',
            position: {
              x: dotsToMm(state.x, dpi),
              y: dotsToMm(state.y, dpi),
            },
            rotation: state.rotation,
            content: content,
            fontSize: fontSizeInPoints,
            font: state.font,
            isVariable: false,
          }
          result.elements.push(textElement)
        }
      }
      // Graphic box ^GB
      else if (cmd.toUpperCase().startsWith('^GB')) {
        const params = parseParams(cmd.substring(3))
        const width = parseInt(params[0]) || 1
        const height = parseInt(params[1]) || 1
        const thickness = parseInt(params[2]) || 1
        const color = params[3]?.toUpperCase() === 'W' ? 'W' : 'B'
        const rounding = parseInt(params[4]) || 0

        // Determine if it's a line or rectangle
        if (height <= thickness * 2 && width > height * 3) {
          // Horizontal line
          const lineElement: LineElement = {
            id: generateId(),
            type: 'line',
            position: {
              x: dotsToMm(state.x, dpi),
              y: dotsToMm(state.y, dpi),
            },
            rotation: state.rotation,
            width: dotsToMm(width, dpi),
            height: dotsToMm(thickness, dpi),
            color: color as 'B' | 'W',
          }
          result.elements.push(lineElement)
        } else if (width <= thickness * 2 && height > width * 3) {
          // Vertical line - use width as thickness, height as length
          // No rotation needed - we represent it with actual dimensions
          const lineElement: LineElement = {
            id: generateId(),
            type: 'line',
            position: {
              x: dotsToMm(state.x, dpi),
              y: dotsToMm(state.y, dpi),
            },
            rotation: state.rotation,
            width: dotsToMm(width, dpi),  // thickness (narrow dimension)
            height: dotsToMm(height, dpi), // length (long dimension)
            color: color as 'B' | 'W',
          }
          result.elements.push(lineElement)
        } else {
          // Rectangle
          const filled = thickness >= Math.min(width, height) / 2
          const rectElement: RectangleElement = {
            id: generateId(),
            type: 'rectangle',
            position: {
              x: dotsToMm(state.x, dpi),
              y: dotsToMm(state.y, dpi),
            },
            rotation: state.rotation,
            width: dotsToMm(width, dpi),
            height: dotsToMm(height, dpi),
            borderThickness: thickness,
            color: color as 'B' | 'W',
            filled,
          }
          result.elements.push(rectElement)
        }
      }
      // Graphic circle ^GC
      else if (cmd.toUpperCase().startsWith('^GC')) {
        const params = parseParams(cmd.substring(3))
        const diameter = parseInt(params[0]) || 10
        const thickness = parseInt(params[1]) || 1
        const color = params[2]?.toUpperCase() === 'W' ? 'W' : 'B'

        const circleElement: CircleElement = {
          id: generateId(),
          type: 'circle',
          position: {
            x: dotsToMm(state.x, dpi),
            y: dotsToMm(state.y, dpi),
          },
          rotation: state.rotation,
          diameter: dotsToMm(diameter, dpi),
          borderThickness: thickness,
          color: color as 'B' | 'W',
        }
        result.elements.push(circleElement)
      }
      // Graphic field (image) ^GFA - skip the data properly
      else if (cmd.toUpperCase().startsWith('^GF')) {
        // ^GFa,b,c,d,data where a=format, b=binary byte count, c=graphic field count, d=bytes per row
        // The data follows and can be very long. We need to skip it.
        // The parser already splits by ^ so the data is included in this cmd
        if (!unsupportedCommands.has('GF')) {
          result.warnings.push('Graphic images (^GF) are not fully supported - image will be skipped')
          unsupportedCommands.add('GF')
        }
        // Just skip this command - the data is already part of cmd
        continue
      }
      // Download graphics ^DG - not supported
      else if (cmd.toUpperCase().startsWith('^DG') || cmd.toUpperCase().startsWith('~DG')) {
        if (!unsupportedCommands.has('DG')) {
          result.warnings.push('Downloaded graphics (^DG/~DG) are not supported and will be skipped')
          unsupportedCommands.add('DG')
        }
      }
      // Recall graphic ^XG - not supported
      else if (cmd.toUpperCase().startsWith('^XG')) {
        if (!unsupportedCommands.has('XG')) {
          result.warnings.push('Recalled graphics (^XG) are not supported and will be skipped')
          unsupportedCommands.add('XG')
        }
      }
      // Field block ^FB - partially supported (creates text without block wrapping)
      else if (cmd.toUpperCase().startsWith('^FB')) {
        // ^FB command sets field block parameters but text still comes via ^FD
        // We ignore the block params for now
      }
      // Field separator ^FS - reset pending
      else if (cmd.toUpperCase().startsWith('^FS')) {
        pendingField = null
      }
      // Skip known but unhandled commands without warning
      else if (
        cmd.toUpperCase().startsWith('^XA') ||
        cmd.toUpperCase().startsWith('^XZ') ||
        cmd.toUpperCase().startsWith('^PQ') ||
        cmd.toUpperCase().startsWith('^LH') ||
        cmd.toUpperCase().startsWith('^LS') ||
        cmd.toUpperCase().startsWith('^LT') ||
        cmd.toUpperCase().startsWith('^MD') ||
        cmd.toUpperCase().startsWith('^MN') ||
        cmd.toUpperCase().startsWith('^MT') ||
        cmd.toUpperCase().startsWith('^PO') ||
        cmd.toUpperCase().startsWith('^PR') ||
        cmd.toUpperCase().startsWith('^CI') ||
        cmd.toUpperCase().startsWith('^FW') ||
        cmd.toUpperCase().startsWith('^FR') ||
        cmd.toUpperCase().startsWith('^FH') ||
        cmd.toUpperCase().startsWith('^FN') ||
        cmd.toUpperCase().startsWith('^FP') ||
        cmd.toUpperCase().startsWith('^FV') ||
        cmd.toUpperCase().startsWith('^SN') ||
        cmd.toUpperCase().startsWith('^MM') ||  // Print mode
        cmd.toUpperCase().startsWith('^JM') ||  // Set dot size
        cmd.toUpperCase().startsWith('^JU') ||  // Configuration update
        cmd.toUpperCase().startsWith('^LR') ||  // Label reverse print
        cmd.toUpperCase().startsWith('~TA') ||  // Tear-off adjust
        cmd.toUpperCase().startsWith('~JS') ||  // Sensor select
        cmd.toUpperCase().startsWith('~SD')     // Set darkness
      ) {
        // These are valid commands but we don't need to process them
        continue
      }
    } catch (e) {
      result.warnings.push(`Failed to parse command: ${cmd.substring(0, 20)}...`)
    }
  }

  return result
}

function createElementFromField(
  pendingField: { type: string; params: string[]; rotation?: Rotation },
  content: string,
  state: FieldState,
  dpi: DPI,
  result: ParseResult
): LabelElement | null {
  const position = {
    x: dotsToMm(state.x, dpi),
    y: dotsToMm(state.y, dpi),
  }

  const rotation = pendingField.rotation || state.rotation

  switch (pendingField.type) {
    case 'barcode-code128':
    case 'barcode-code39':
    case 'barcode-ean13': {
      const params = pendingField.params
      const height = parseInt(params[0]) || 50
      const showText = params[1] !== 'N'
      const moduleWidth = parseInt(params[2]) || state.barcodeModuleWidth

      const barcodeElement: BarcodeElement = {
        id: generateId(),
        type: pendingField.type as BarcodeElement['type'],
        position,
        rotation,
        content: content,
        height: dotsToMm(height, dpi),
        moduleWidth,
        showText,
        isVariable: false,
      }
      return barcodeElement
    }

    case 'qrcode': {
      const params = pendingField.params
      const magnification = parseInt(params[1]) || 4
      const ecLevel = params[2]?.toUpperCase() || 'M'

      // QR code content often has format: MA,<data> or just <data>
      let qrContent = content
      if (content.startsWith('MA,') || content.startsWith('QA,') || content.startsWith('LA,') || content.startsWith('HA,')) {
        qrContent = content.substring(3)
      }

      const qrElement: QRCodeElement = {
        id: generateId(),
        type: 'qrcode',
        position,
        rotation,
        content: qrContent,
        magnification,
        errorCorrection: (['H', 'Q', 'M', 'L'].includes(ecLevel) ? ecLevel : 'M') as QRCodeElement['errorCorrection'],
        isVariable: false,
      }
      return qrElement
    }

    case 'datamatrix': {
      const params = pendingField.params
      const size = parseInt(params[0]) || 4

      const dmElement: DataMatrixElement = {
        id: generateId(),
        type: 'datamatrix',
        position,
        rotation,
        content: content,
        size,
        isVariable: false,
      }
      return dmElement
    }

    default:
      result.warnings.push(`Unsupported barcode type: ${pendingField.type}`)
      return null
  }
}

// Validate ZPL format
export function isValidZPL(code: string): boolean {
  const normalized = code.trim().toUpperCase()
  return normalized.includes('^XA') && normalized.includes('^XZ')
}
